/**
 * Public URL segments.
 *
 * A slug is a permanent public address — `goodbuild.cc/dev/tiny`,
 * `goodbuild.cc/g/tiny-city` — so it is constrained rather than sanitised into
 * something plausible. `suggestSlug` proposes; `slugProblem` decides.
 *
 * Shared by the browser and the server on purpose. The onboarding flow
 * pre-generates an address from the studio name so the person only has to
 * accept or change it, and the value it shows has to be the value the server
 * action will accept — two implementations of the same rule would eventually
 * propose something the action then rejects, which is the worst moment to find
 * out.
 */

export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

export const SLUG_MIN = 2;
export const SLUG_MAX = 48;

/**
 * Addresses the platform needs for itself.
 *
 * These are top-level route segments across the three sites. A studio that took
 * `/dev/admin` would be harmless today and a collision the first time that
 * route exists, which is not a bug anybody would connect to a studio registered
 * months earlier.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'about',
  'admin',
  'api',
  'board',
  'browse',
  'c',
  'contact',
  'creator',
  'creators',
  'dev',
  'developer',
  'developers',
  'g',
  'legal',
  'library',
  'new',
  'onboarding',
  'orders',
  'portal',
  'pub',
  'settings',
  'sign-in',
  'sign-up',
  'store',
  'support',
]);

/**
 * A slug proposed from a display name.
 *
 * Accents are folded rather than dropped, so "Ćma Studio" proposes `cma-studio`
 * and not `ma-studio`: NFD splits each accented letter into a base letter and a
 * combining mark, and `\p{M}` removes the marks and leaves the letters.
 * Everything else that is not a letter or a digit becomes a hyphen, runs
 * collapse, and the ends are trimmed — the pattern above requires the first and
 * last characters to be alphanumeric.
 *
 * Returns an empty string when nothing survives, which the caller shows as an
 * empty field rather than as a suggestion of `-` or `untitled`.
 */
export function suggestSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, '');
}

/**
 * Why a slug is unacceptable, or null when it is fine.
 *
 * Messages are written to be read by the person typing, so they say what to do
 * rather than what went wrong.
 */
export function slugProblem(slug: string): string | null {
  if (slug.length < SLUG_MIN) return `The address needs at least ${SLUG_MIN} characters.`;
  if (slug.length > SLUG_MAX) return 'That address is too long.';
  if (!SLUG_PATTERN.test(slug)) {
    return 'Use lowercase letters, numbers and hyphens, starting and ending with a letter or number.';
  }
  if (RESERVED_SLUGS.has(slug)) return 'That address is reserved.';
  return null;
}
